/* eslint-disable max-len */
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Info } from '@one-thd/sui-icons';
import { Link, Typography } from '@one-thd/sui-atomic-components';
import { bool, string } from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { clickEvent, loadEvent } from '../../utils/publisher';
import {
  getPaymentEstimatorDetails,
  SPECIAL_PROMOTION,
  TIERED_PROMOTION,
  APPLIANCE_PROMOTION,
  TIERED_APPLIANCE_PROMOTION,
} from '../../utils/plcc-promo-info';
import { useContentfulContext } from '../../../providers/ContentfulContext';
import { PLCN_CARD_IMAGE_SMALL } from '../../utils/constants';
import TermsAndConditionsDrawer from '../../common/terms-and-conditions-drawer.component';
import { TermsAndConditionsDrawerContentful } from '../../common/terms-and-conditions-drawer-contentful.component';
import { formatNumber } from '../../utils/general';

export const PaymentEstimator = ({
  campaignCode, isMobile, price, isAppliance, showEverydayFinancing, isPlp
}) => {
  const modalRef = useRef({});
  const [displayDrawer, setDisplayDrawer] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);

  const { plccData } = useContentfulContext();
  const { isConsumerApp } = useContext(ExperienceContext);

  function updatePaymentEstimatorDetails() {
    setPaymentDetails(getPaymentEstimatorDetails(
      plccData, campaignCode, isMobile, isAppliance, price
    ));
  }

  useEffect(() => {
    if (plccData) {
      loadEvent('plcc 850+ est');
      updatePaymentEstimatorDetails();
    }
  }, [plccData]);

  const {
    applyNowUrl, monthlyPayment, financingMonths, activePromotion, activeEndDate
  } = paymentDetails || {};

  const closeDrawer = () => {
    setDisplayDrawer(false);
  };

  const applyNowClick = () => {
    clickEvent('plcc 850+ est');
    return isConsumerApp ? window.open(applyNowUrl, 'self') : window.open(applyNowUrl, '_blank');
  };

  const everydayFinancingCopy = () => {
    switch (activePromotion) {
    case SPECIAL_PROMOTION:
      return '12 Months Special Financing** available on purchases of $299 or more. ';
    case TIERED_PROMOTION:
      return 'Up to 24 Months Special Financing** available on purchases of $299 or more. ';
    case APPLIANCE_PROMOTION:
      return '12 Months Special Financing** available on Appliance purchases of $299 or more. ';
    case TIERED_APPLIANCE_PROMOTION:
      return 'Up to 24 Months Special Financing** available on Appliance purchases of $299 or more. ';
    default:
      return '6 Months Everyday Financing** available on purchases of $299 or more. ';
    }
  };

  const endDateCopy = () => {
    const prefix = 'Ends ';
    const formattedDate = activeEndDate ? (() => {
      const date = new Date(activeEndDate);
      const mm = ('0' + (date.getMonth() + 1)).slice(-2);
      const dd = ('0' + date.getDate()).slice(-2);
      return `${mm}/${dd} `;
    })() : '';
    return activeEndDate ? prefix + formattedDate : '';
  };

  const handlePlpVerbage = () => {
    if (showEverydayFinancing) {
      return everydayFinancingCopy();
    }
    if (isPlp) {
      return (
        <>
          <b>${formatNumber(monthlyPayment)}/mo**</b> w/ {financingMonths}mos** financing. {endDateCopy()}
        </>
      );
    }
    return (
      <>
        <b>${formatNumber(monthlyPayment)}</b>/mo** suggested payments with {financingMonths} mos** financing. {endDateCopy()}
      </>
    );
  };

  return (
    <>
      <div className="sui-inline-flex sui-items-center">
        <img
          src={PLCN_CARD_IMAGE_SMALL}
          alt="Apply Now"
          className="sui-pr-2"
          width="50"
          height="50"
        />
        <div>
          <span>
            {handlePlpVerbage()}
            {isPlp && (
              <span className="sui-pr-1 sui-cursor-pointer sui-relative sui-down-1">
                <Info
                  aria-label="Credit Offer Details"
                  color="info"
                  onClick={setDisplayDrawer}
                  size="small"
                />
              </span>
            )}
          </span>
          {!isPlp && (
            <>
              <span className="sui-pr-1 sui-cursor-pointer sui-relative sui-down-1">
                <Info
                  aria-label="Credit Offer Details"
                  color="info"
                  onClick={setDisplayDrawer}
                  size="small"
                />
              </span>
              <span>
                <Link
                  onClick={applyNowClick}
                  color="brand"
                  variant="body-sm"
                  id="credit-promo-apply-now"
                >
                  <span className="sui-whitespace-nowrap">
                    Apply Now
                  </span>
                </Link>
              </span>
            </>
          )}
        </div>
        {
          isPlp
            ? (
              <TermsAndConditionsDrawer
                closeDrawer={closeDrawer}
                open={displayDrawer}
                showFinancing
                applyNowClick={() => clickEvent('plcc-est')}
                isAppliance={isAppliance}
                isHDHome={false}
                price={price}
                isCountertops={false}
                isPlp={isPlp}
              />
            )
            : (

              ((applyNowUrl && displayDrawer) && (
                <TermsAndConditionsDrawerContentful
                  closeDrawer={closeDrawer}
                  open={displayDrawer}
                  showFinancing
                  applyNowClick={() => clickEvent('plcc-est')}
                  isAppliance={isAppliance}
                  campaignCode={campaignCode}
                  price={price}
                  isPlp={isPlp}
                />
              ))
            )
        }
      </div>
    </>
  );
};

PaymentEstimator.displayName = 'PaymentEstimator';

PaymentEstimator.propTypes = {
  campaignCode: string.isRequired,
  isMobile: bool,
  price: string,
  isAppliance: bool,
  showEverydayFinancing: bool,
  isPlp: bool
};

PaymentEstimator.defaultProps = {
  price: null,
  isMobile: false,
  isAppliance: false,
  showEverydayFinancing: false,
  isPlp: false
};
